import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, TOKEN_KEY } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { REGION_NAMES, comunasOf } from "../../lib/chile";
import { CompanyUsers } from "../../components/CompanyUsers";
import { Buildings, UploadSimple, CircleNotch } from "@phosphor-icons/react";

function fileUrl(id) {
  return id ? `${API}/files/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}` : null;
}

export default function CarrierCompany() {
  const { user } = useAuth();
  const isOwner = !user?.parentCarrierId;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/carrier/profile").then((r) => {
    const p = r.data;
    setForm({
      companyType: p.companyType || "JURIDICA", companyName: p.companyName || "", companyRut: p.companyRut || "",
      companyAddress: p.companyAddress || "", companyRegion: p.companyRegion || "", companyComuna: p.companyComuna || "",
      companyPhone: p.companyPhone || "", companyLogoFileId: p.companyLogoFileId || null,
    });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, companyLogoFileId: up.data.id }));
      toast.success("Logo cargado");
    } catch { toast.error("Error al subir el logo"); }
    setUploading(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/carrier/profile", form);
      toast.success("Datos de la empresa guardados");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  if (!form) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const isNatural = form.companyType === "NATURAL";

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="carrier-company">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Acreditación</span>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-4">Mi Empresa</h1>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="company-logo">
            {form.companyLogoFileId
              ? <img src={fileUrl(form.companyLogoFileId)} alt="Logo" className="w-full h-full object-contain" />
              : <Buildings size={28} className="text-zinc-300" />}
          </div>
          <label className="text-sm font-semibold text-[#FF5A36] cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid="company-logo-upload">
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} {form.companyLogoFileId ? "Cambiar logo" : "Subir logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
          </label>
        </div>

        <div className="mb-4">
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Tipo de empresa</label>
          <div className="flex gap-2 mt-1.5">
            {[["JURIDICA", "Persona Jurídica"], ["NATURAL", "Persona Natural"]].map(([val, lbl]) => (
              <button key={val} type="button" data-testid={`company-type-${val.toLowerCase()}`}
                onClick={() => setForm({ ...form, companyType: val })}
                className={`px-5 py-2 rounded-xl text-sm font-semibold border transition-colors ${form.companyType === val ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white border-zinc-300 text-zinc-600 hover:border-[#0F1115]"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
          <Field label={isNatural ? "Nombre completo" : "Razón social"} testid="company-name" value={form.companyName} onChange={(v) => setForm({ ...form, companyName: v })} placeholder={isNatural ? "Nombre y apellido" : "Transportes Ltda."} />
          <Field label={isNatural ? "RUT" : "RUT empresa"} testid="company-rut" value={form.companyRut} onChange={(v) => setForm({ ...form, companyRut: v })} placeholder="76.543.210-K" />
          <Field label="Teléfono" testid="company-phone" value={form.companyPhone} onChange={(v) => setForm({ ...form, companyPhone: v })} placeholder="+56 9 ..." />
          <Field label="Dirección" testid="company-address" value={form.companyAddress} onChange={(v) => setForm({ ...form, companyAddress: v })} placeholder="Calle 123" />
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Región</label>
            <select data-testid="company-region" value={form.companyRegion}
              onChange={(e) => setForm({ ...form, companyRegion: e.target.value, companyComuna: "" })}
              className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
              <option value="">Selecciona</option>
              {REGION_NAMES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Comuna</label>
            <select data-testid="company-comuna" value={form.companyComuna} disabled={!form.companyRegion}
              onChange={(e) => setForm({ ...form, companyComuna: e.target.value })}
              className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:bg-zinc-50">
              <option value="">{form.companyRegion ? "Selecciona" : "Primero elige región"}</option>
              {comunasOf(form.companyRegion).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="company-save"
          className="mt-5 px-6 py-2.5 rounded-full bg-[#0F1115] text-white text-sm font-semibold hover:bg-[#1A1D24] transition-colors active:scale-95 disabled:opacity-60">
          {busy ? "Guardando…" : "Guardar datos de la empresa"}
        </button>
      </div>

      {isOwner && <CompanyUsers permsKey="carrierPermissions" isNatural={isNatural} />}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full h-12 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
    </div>
  );
}
